if (!customElements.get('pirpamer-hero-images')) {
  customElements.define('pirpamer-hero-images', class extends HTMLElement {
    connectedCallback() {
      // Deferred scripts and theme-editor section reloads both reach this point.
      this.frames = [...this.querySelectorAll('[data-hero-image]')];
      if (this.frames.length < 2 || this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.index = Math.max(0, this.frames.findIndex(frame => frame.classList.contains('is-current')));
      this.dots = [...this.querySelectorAll('[data-hero-image-dot]')];
      this.toggle = this.querySelector('[data-hero-image-toggle]');
      this.status = this.querySelector('[data-hero-image-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.hovered = false;
      this.focused = false;
      this.inView = !('IntersectionObserver' in window);
      this.slide = this.closest('[data-hero-slide]');
      this.controls = this.querySelector('[data-hero-image-controls]');
      this.controls.hidden = false;
      this.querySelector('[data-hero-image-previous]').addEventListener('click', () => this.show(this.index - 1), options);
      this.querySelector('[data-hero-image-next]').addEventListener('click', () => this.show(this.index + 1), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index), options));
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updateToggle();
        this.start();
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.stop();
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.start(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.stop(); }, options);
      this.addEventListener('focusout', event => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.start();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.addEventListener('pointerdown', event => {
        this.touch = event.pointerType === 'touch' && !event.target.closest('button')
          ? { x: event.clientX, y: event.clientY } : null;
      }, options);
      this.addEventListener('pointerup', event => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x;
        const dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
      }, options);
      this.addEventListener('pointercancel', () => { this.touch = null; }, options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.updateToggle();
        this.start();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!event.target.contains(this)) return;
        this.editorSelected = true;
        this.stop();
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (!event.target.contains(this)) return;
        this.editorSelected = false;
        this.start();
      }, options);
      if (this.slide) {
        this.slideObserver = new MutationObserver(() => this.start());
        this.slideObserver.observe(this.slide, { attributes: true, attributeFilter: ['hidden'] });
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.start();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      this.updateToggle();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.requestId = (this.requestId || 0) + 1;
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.slideObserver?.disconnect();
    }

    updateToggle() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      this.toggle.setAttribute('aria-label', this.paused ? 'Bildwechsel starten' : 'Bildwechsel pausieren');
      this.toggle.querySelector('[data-hero-image-pause]').toggleAttribute('hidden', this.paused);
      this.toggle.querySelector('[data-hero-image-play]').toggleAttribute('hidden', !this.paused);
    }

    start() {
      this.stop();
      if (this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.focused ||
          !this.inView || document.hidden || this.slide?.hidden || this.editorSelected) return;
      const seconds = Math.max(4, Math.min(12, Number(this.dataset.interval) || 6));
      this.timer = window.setTimeout(() => this.show(this.index + 1, false), seconds * 1000);
    }

    stop() { window.clearTimeout(this.timer); }

    async show(index, announce = true) {
      this.stop();
      const requested = (index + this.frames.length) % this.frames.length;
      const requestId = this.requestId = (this.requestId || 0) + 1;
      const image = this.frames[requested].querySelector('img');
      if (image) {
        image.loading = 'eager';
        // Keep the current photograph visible while a slower connection loads the next.
        try { await image.decode(); } catch {
          // A missing file must not replace the current photo with a broken image.
          if (!image.naturalWidth) { this.start(); return; }
        }
      }
      if (!this.isConnected || this.requestId !== requestId) return;
      this.index = requested;
      this.frames.forEach((frame, frameIndex) => {
        const active = frameIndex === this.index;
        if (active) frame.querySelector('img')?.setAttribute('loading', 'eager');
        frame.classList.toggle('is-current', active);
        frame.setAttribute('aria-hidden', String(!active));
        this.dots[frameIndex]?.setAttribute('aria-current', String(active));
      });
      if (announce) this.status.textContent = `Bild ${this.index + 1} von ${this.frames.length}`;
      this.start();
    }
  });
}
